"use client";

import { useEffect, useState } from "react";
import { useSiteConfig } from "@/lib/SiteConfigContext";
import { DEFAULT_IMAGES, type ImageSlot } from "@/lib/siteConfig";

// Renders an admin-managed image slot. A small client island so pages can stay
// server components — the live photo (or its baked-in placeholder) is read from
// the SiteConfig provider at runtime. If the configured photo fails to load
// (e.g. a custom upload that was later deleted), fall back to the slot's
// baked-in placeholder instead of rendering a broken image.
export default function ConfigImage({
  slot,
  alt,
  className,
  eager = false,
  priority = false,
}: {
  slot: ImageSlot;
  alt: string;
  className?: string;
  eager?: boolean;
  /** LCP image: load eagerly AND raise fetch priority (e.g. the homepage hero). */
  priority?: boolean;
}) {
  const { images } = useSiteConfig();
  const configured = images[slot];
  const placeholder = DEFAULT_IMAGES[slot];
  const [src, setSrc] = useState(configured);

  // Re-sync when the live config changes (CMS publish → 60s poll) so a new URL
  // gets a fresh chance before any fallback applies.
  useEffect(() => {
    setSrc(configured);
  }, [configured]);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      className={className}
      loading={eager || priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      onError={() => {
        // Guard against a loop if the placeholder itself is unavailable.
        if (src !== placeholder) setSrc(placeholder);
      }}
    />
  );
}
